import { useVerifyAffiliateCodeQuery } from "Api/Queries/Affiliate/useVerifyAffiliateCodeQuery";
import {
  type AffiliateCodeError,
  getAffiliateCodeError,
} from "Components/Affiliate/AffiliateCodeField/affiliateCodeErrors";
import type { AffiliateUserRole } from "Types/types";
import { useState } from "react";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

type Options = {
  role: AffiliateUserRole;
  /**
   * Off while the active affiliate is unknown or locks the field — a code carried in from an
   * affiliate link is then not verified for nothing.
   */
  isEnabled: boolean;
};

/**
 * State of an affiliate code that is only verified here and sent on with the flow's own submit
 * (Customer last step, Merchant add-role form). A code carried in from an affiliate link starts
 * the field and is verified as soon as the hook is enabled. `verifiedCode` is the code to send:
 * the stored form of a code the backend confirmed, null otherwise. A typed code that is not
 * verified must stop the submit (`hasUnverifiedCode`), or it would be dropped without a word.
 */
export const useAffiliateCodeVerification = ({ role, isEnabled }: Options) => {
  const [linkCode] = useState(() =>
    getLocalStorageItem(LocalStorageKeys.AffiliateCode),
  );
  const [inputValue, setInputValue] = useState(linkCode ?? "");
  const [submittedCode, setSubmittedCode] = useState<string | null>(
    linkCode?.trim() || null,
  );
  const [isUnverifiedErrorShown, setIsUnverifiedErrorShown] = useState(false);
  const { data, error, isFetching, refetch } = useVerifyAffiliateCodeQuery(
    role,
    isEnabled ? submittedCode : null,
  );

  // A changed code is no longer the one verified or rejected, so its error goes with it.
  const changeInput = (value: string) => {
    setInputValue(value);
    setSubmittedCode(null);
    setIsUnverifiedErrorShown(false);
  };

  const verify = () => {
    const code = inputValue.trim();
    if (!code) {
      return;
    }

    setIsUnverifiedErrorShown(false);
    if (code === submittedCode) {
      refetch();
      return;
    }

    setSubmittedCode(code);
  };

  const remove = () => {
    setInputValue("");
    setSubmittedCode(null);
    setIsUnverifiedErrorShown(false);
  };

  const verification = isEnabled && submittedCode && data ? data : null;
  const verificationError =
    isEnabled && submittedCode && error ? getAffiliateCodeError(error) : null;
  const fieldError: AffiliateCodeError | null =
    verificationError ?? (isUnverifiedErrorShown ? "Unverified" : null);

  return {
    fieldProps: {
      inputValue,
      onInputChange: changeInput,
      onVerify: verify,
      onRemove: remove,
      verification,
      error: fieldError,
      isVerifying: isFetching,
    },
    verifiedCode: verification?.code ?? null,
    hasUnverifiedCode: isEnabled && !!inputValue.trim() && !verification,
    showUnverifiedError: () => setIsUnverifiedErrorShown(true),
  };
};
